import { useState } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FlaskConical, Loader2, Crown, ArrowRight } from "lucide-react";
import { SCENARIOS } from "@/data/staticData";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";

const riskColors = { high: "bg-amber-100 text-amber-800 border-amber-200", limited: "bg-blue-100 text-blue-800 border-blue-200", minimal: "bg-green-100 text-green-800 border-green-200", unacceptable: "bg-red-100 text-red-800 border-red-200" };
const riskLabels = { high: "Eleve", limited: "Limite", minimal: "Minimal", unacceptable: "Inacceptable" };

export default function SimulatorPage() {
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  const [custom, setCustom] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const runSimulation = async (title, desc) => {
    setLoading(true);
    setAnalysis(null);
    try {
      const { data } = await API.post("/simulate", { scenario_title: title, custom_description: desc || null });
      setAnalysis(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la simulation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="simulator-page">
      <div className="mb-8 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <FlaskConical className="h-6 w-6 text-[var(--ia-navy)]" />
          <h1 className="text-2xl font-bold text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Simulateur d'analyse IA</h1>
        </div>
        <p className="text-[var(--ia-text-muted)]">Analysez un cas d'usage IA selon l'AI Act et le RGPD</p>
      </div>

      {!analysis ? (
        <div className="space-y-6 animate-fade-in">
          <h3 className="text-lg font-semibold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Scénarios prédéfinis</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SCENARIOS.map((sc) => (
              <Card key={sc.id} className={`cursor-pointer transition-all duration-200 hover:-translate-y-px hover:shadow-sm border-[var(--ia-border)] ${selected?.id === sc.id ? "ring-2 ring-[var(--ia-blue)]" : ""}`}
                onClick={() => setSelected(sc)} data-testid={`scenario-${sc.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold text-sm">{sc.title}</h4>
                    <Badge variant="outline" className={`text-[10px] ${riskColors[sc.risk]}`}>{riskLabels[sc.risk]}</Badge>
                  </div>
                  <p className="text-xs text-[var(--ia-text-muted)]">{sc.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="border-t border-[var(--ia-border)] pt-6">
            <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Ou décrivez votre propre cas</h3>
            <Textarea data-testid="simulator-custom-input" rows={3} placeholder="Décrivez votre projet IA à analyser..." value={custom} onChange={(e) => setCustom(e.target.value)} className="resize-none mb-4" />
          </div>

          <div className="flex gap-3">
            {selected && (
              <Button onClick={() => runSimulation(selected.title, selected.description)} disabled={loading} data-testid="simulator-run-selected-btn" className="bg-[var(--ia-navy)]">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                Analyser "{selected.title}"
              </Button>
            )}
            {custom.trim() && (
              <Button onClick={() => runSimulation("Cas personnalise", custom)} disabled={loading} data-testid="simulator-run-custom-btn" variant={selected ? "outline" : "default"} className={!selected ? "bg-[var(--ia-navy)]" : ""}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                Analyser mon cas
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Analyse : {analysis.scenario}</h3>
            <Button variant="outline" onClick={() => { setAnalysis(null); setSelected(null); setCustom(""); }} data-testid="simulator-new-btn">
              Nouvelle simulation
            </Button>
          </div>
          <Card className="border-[var(--ia-border)]">
            <CardContent className="p-6">
              <div className="prose prose-sm max-w-none prose-headings:font-semibold" data-testid="simulator-result">
                <ReactMarkdown>{analysis.analysis}</ReactMarkdown>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
